import { useMemo, useState } from "react";
import { LoadingPulse, BlockingActionOverlay } from "../../ui";
import {
  SLOT_CLASS,
  actionableSlotClasses,
  displaySlotValue,
  logEventDescription,
  slotClassLabel,
  slotClassTone,
  slotSoftoneChangeLocked,
  softoneChangeLockLabel,
} from "../../lib/softoneSlotSync";
import { formatScheduleChangeTime, scheduleChangeRowDescription } from "../schedule/scheduleComponents";
import { FoxlineSlotPopup } from "../schedule/FoxlineSlotPopup";
import { WorkMonitorCacheBar } from "../schedule/WorkMonitorCacheBar";
import { canPushFoxline } from "../../identity/access";

const QUEUE_ORDER = actionableSlotClasses();

export function FoxlineSlotSyncPanel({
  lang,
  user,
  sync,
  employeeNameFor,
  /** SoftOne API writes only after T3 → pending_hr / post_pending_hr. */
  softoneWritesAllowed = false,
}) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const isHr = canPushFoxline(user);
  const canWriteSoftone = isHr && softoneWritesAllowed && !sync?.readOnly;
  const [sourceFilter, setSourceFilter] = useState("all");
  const [openSlotKey, setOpenSlotKey] = useState(null);
  const {
    loading,
    transmitting,
    actionBusy,
    error,
    classifiedSlots,
    grouped,
    slotByKey,
    historyBySlot,
    firstSent,
    preexisting,
    locationId,
    readOnly,
    monitor,
    refreshMonitor,
    firstSend,
    uploadEligible,
    restoreDeleted,
    replaceModified,
    pushSoftoneDiffs,
    markSubmitted,
    acceptDeletion,
    resendCount = 0,
    pushDiffCount = 0,
  } = sync;

  const openSlot = openSlotKey ? slotByKey[openSlotKey] : null;
  const allHistory = useMemo(() => {
    const rows = [];
    classifiedSlots.forEach((s) => {
      (historyBySlot[s.key] || []).forEach((row) => {
        rows.push({ ...row, _slotKey: s.key, employee_code: s.employee_code, employee_name: s.employee_name, rota_date: s.date });
      });
    });
    rows.sort((a, b) => String(b.changed_at || "").localeCompare(String(a.changed_at || "")));
    return rows;
  }, [classifiedSlots, historyBySlot]);

  const filteredHistory = allHistory.filter((row) => {
    if (sourceFilter === "all") return true;
    return String(row.source || "local").toLowerCase() === sourceFilter;
  });

  async function handleFirstSend() {
    try {
      await firstSend();
    } catch {
      /* error surfaced on sync.error */
    }
  }

  return (
    <div>
      <BlockingActionOverlay
        open={Boolean(transmitting)}
        title={t("Sending to SoftOne", "Αποστολή στο SoftOne")}
        subtitle={t("Do not go back or refresh this page.", "Μην πατήσετε πίσω και μην ανανεώσετε τη σελίδα.")}
      />
      {error ? (
        <div style={{ background: "#FEF0ED", border: "1px solid #FACFC8", color: "var(--danger)", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)" }}>
              {t("SoftOne work monitor", "SoftOne work monitor")}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
              {firstSent
                ? t("Order: save Internal first, then SoftOne. Blank SoftOne slots only on upload; use Push/Resend for occupied drifts.", "Σειρά: αποθήκευση Εσωτερικού πρώτα, μετά SoftOne. Αποστολή μόνο σε κενά SoftOne· Push/Resend για occupied.")
                : t("Order: save Internal first, then SoftOne. Live SoftOne is checked first; only blank slots are sent.", "Σειρά: αποθήκευση Εσωτερικού πρώτα, μετά SoftOne. Ελέγχεται πρώτα το live SoftOne· στέλνονται μόνο κενά slots.")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {readOnly ? (
              <div style={{ padding: "8px 10px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #F59E0B", color: "#92400E", fontSize: 12, fontWeight: 700 }}>
                {t("Prod simulation — SoftOne reads only. Uploads are disabled.", "Προσομοίωση παραγωγής — μόνο ανάγνωση SoftOne. Οι αποστολές είναι απενεργοποιημένες.")}
              </div>
            ) : null}
            {isHr && !readOnly && !softoneWritesAllowed ? (
              <div style={{ padding: "8px 10px", borderRadius: 10, background: "#F3F4F6", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: 12, fontWeight: 700 }}>
                {t(
                  "SoftOne upload unlocks after T3 approval (Pending HR upload).",
                  "Η αποστολή SoftOne ξεκλειδώνει μετά την έγκριση T3 (Αναμονή ανεβάσματος HR)."
                )}
              </div>
            ) : null}
            {canWriteSoftone && !firstSent ? (
              <button
                type="button"
                className="btn-base btn-green"
                disabled={actionBusy || locationId == null || !monitor.available}
                title={
                  locationId == null
                    ? t("No SoftOne location mapping", "Δεν υπάρχει αντιστοίχιση SoftOne")
                    : !monitor.available
                      ? t("Need a live work-monitor fetch before first send", "Απαιτείται live work monitor πριν την πρώτη αποστολή")
                      : undefined
                }
                onClick={handleFirstSend}
                style={{ minHeight: 36 }}
              >
                {t("Send week to SoftOne", "Αποστολή εβδομάδας στο SoftOne")}
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <WorkMonitorCacheBar
            lang={lang}
            monitor={monitor}
            actionsCache={sync.actionsCache}
            loading={loading}
            refreshing={sync.refreshingMonitor}
            fetchingActions={sync.fetchingActions}
            buttonsDisabled={actionBusy}
            error={error}
            notice={sync.writeNotice}
            onRefresh={() => refreshMonitor()}
            onFetchActions={() => sync.fetchActions()}
          />
        </div>

        {preexisting.length > 0 ? (
          <div style={{ marginTop: 12, padding: 10, background: "#EDE9FE", border: "1px solid #C4B5FD", borderRadius: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#5B21B6" }}>
              {t(`${preexisting.length} slot(s) already filled in SoftOne — they will not be sent`, `${preexisting.length} slots ήδη συμπληρωμένα στο SoftOne — δεν θα σταλούν`)}
            </div>
            <div style={{ fontSize: 11, color: "#5B21B6", marginTop: 4, fontWeight: 600 }}>
              {t("Internal edits on those cells stay unpublished until SoftOne is blank, you resend (cancel + upload), or you update SoftOne offline.", "Οι εσωτερικές αλλαγές μένουν αδημοσίευτες μέχρι το SoftOne να είναι κενό, να γίνει επαναποστολή (ακύρωση + αποστολή), ή ενημέρωση εκτός εργαλείου.")}
            </div>
          </div>
        ) : null}

        {canWriteSoftone && firstSent && grouped[SLOT_CLASS.API_ELIGIBLE]?.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn-base btn-green"
              disabled={actionBusy}
              onClick={() => uploadEligible()}
              style={{ minHeight: 36 }}
            >
              {t(`Upload ${grouped[SLOT_CLASS.API_ELIGIBLE].length} blank slot(s)`, `Αποστολή ${grouped[SLOT_CLASS.API_ELIGIBLE].length} κενών slots`)}
            </button>
          </div>
        ) : null}
        {canWriteSoftone && pushDiffCount > 0 ? (
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn-base btn-green"
              disabled={actionBusy}
              onClick={() => pushSoftoneDiffs()}
              style={{ minHeight: 36 }}
            >
              {t(
                `Push ${pushDiffCount} SoftOne update(s)`,
                `Αποστολή ${pushDiffCount} ενημερώσεων SoftOne`
              )}
            </button>
            {resendCount > 0 && resendCount !== pushDiffCount ? (
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={actionBusy}
                onClick={() => replaceModified()}
                style={{ minHeight: 36 }}
              >
                {t(
                  `Resend ${resendCount} occupied`,
                  `Επαναποστολή ${resendCount} occupied`
                )}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? (
        <LoadingPulse size={22} />
      ) : (
        QUEUE_ORDER.map((cls) => {
          const rows = grouped[cls] || [];
          if (rows.length === 0) return null;
          const tone = slotClassTone(cls);
          return (
            <div key={cls} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: tone.color, marginBottom: 8 }}>
                {slotClassLabel(cls, lang)} · {rows.length}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rows.map((slot) => (
                  <button
                    key={slot.key}
                    type="button"
                    onClick={() => setOpenSlotKey(slot.key)}
                    style={{
                      textAlign: "left",
                      border: `1px solid ${tone.border}`,
                      background: tone.bg,
                      borderRadius: 10,
                      padding: "10px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-primary)" }}>
                      {employeeNameFor?.(slot.employee_code) || slot.employee_name} · {slot.date}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3 }}>
                      {t("Internal", "Εσωτερικό")} {displaySlotValue(slot.local, lang)}
                      {" · "}
                      {t("Sent", "Στάλθηκε")} {displaySlotValue(slot.submitted, lang)}
                      {" · "}
                      SoftOne {displaySlotValue(slot.foxline, lang)}
                      {slotSoftoneChangeLocked(slot) ? ` · ${softoneChangeLockLabel(lang)}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })
      )}

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
            {t("Full history (Internal + upload)", "Πλήρες ιστορικό (Εσωτερικό + αποστολές)")}
          </div>
          <select className="form-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ minWidth: 140 }}>
            <option value="all">{t("All sources", "Όλες οι πηγές")}</option>
            <option value="local">{t("Internal", "Εσωτερικό")}</option>
            <option value="upload">{t("Upload", "Αποστολές")}</option>
            <option value="foxline">SoftOne</option>
          </select>
        </div>
        {filteredHistory.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {t("No events yet.", "Δεν υπάρχουν γεγονότα ακόμα.")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredHistory.slice(0, 80).map((row, i) => (
              <div key={row.id || i} style={{ border: "1px solid var(--border)", background: "var(--surface2)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
                  {row.employee_name || row.employee_code} · {row.rota_date || row.date || row.day_key}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                  {logEventDescription(row, lang) || scheduleChangeRowDescription(row, lang)}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                  {formatScheduleChangeTime(row.changed_at)} {row.changed_by_name ? (lang === "gr" ? `από ${row.changed_by_name}` : `by ${row.changed_by_name}`) : ""}
                  {row.source ? ` · ${row.source}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FoxlineSlotPopup
        open={Boolean(openSlot)}
        onClose={() => setOpenSlotKey(null)}
        slot={openSlot}
        history={openSlot ? historyBySlot[openSlot.key] || [] : []}
        lang={lang}
        isHr={isHr}
        readOnly={readOnly || !softoneWritesAllowed}
        busy={actionBusy}
        onUploadBlank={() => openSlot && uploadEligible([openSlot.key])}
        onMarkSubmitted={() => openSlot && markSubmitted([openSlot.key])}
        onAcceptDeletion={() => openSlot && acceptDeletion([openSlot.key])}
        onRestore={() => openSlot && restoreDeleted([openSlot.key])}
        onReplace={() => openSlot && replaceModified([openSlot.key])}
      />
    </div>
  );
}
